import Service, { service } from '@ember/service';
import { action } from '@ember/object';
import * as Sentry from '@sentry/ember';
import dragula from 'dragula';
import 'dragula/dist/dragula.css';
import { neighborsOf, positionBetween } from 'scaleflow/utils/position';
import {
  flipAnimate,
  snapshotPositions,
} from 'scaleflow/utils/flip-animate-siblings';
import {
  convertListItem,
  isConvertibleType,
} from 'scaleflow/utils/convert-list-item';

/**
 * Owns the item-level dragula instance for a *scope*.
 *
 * dragula can only move an element between containers belonging to the same
 * drake, so a page that wants to drag items between several `List::List`
 * components (the team check-in page) needs one drake living above all of them.
 * Lists sharing a `@dragScope` join the same scope; a list without one gets a
 * private scope, which behaves exactly like the component-local drake this
 * replaced.
 */
export default class ListDragService extends Service {
  @service store;

  // scope name -> { drake, containers: Map<Element, List::List component>, flipPositions }
  scopes = new Map();

  scopeFor(name) {
    let scope = this.scopes.get(name);
    if (scope) return scope;

    scope = {
      containers: new Map(),
      flipPositions: new Map(),
      drake: null,
    };
    scope.drake = this.buildDrake(name, scope);
    this.scopes.set(name, scope);
    return scope;
  }

  buildDrake(name, scope) {
    const drake = dragula({
      containers: [],
      revertOnSpill: true,
      direction: 'vertical',
      moves: (_el, _container, handle) =>
        handle?.classList?.contains('item-sort-icon') ?? false,
      accepts: (_el, target, source) => this.accepts(scope, target, source),
    });

    drake.on('drop', (el, target, source) =>
      this.onDrop(scope, el, target, source),
    );

    // dragula repositions siblings live as you drag (its 'shadow' event fires
    // right after each reposition, per its own source), with no animation of
    // its own. FLIP-animate those shifts: snapshot positions at drag start,
    // then on every subsequent reposition, animate the delta from the last
    // snapshot and save the new one as the baseline for next time. Items can
    // move across several containers, so the baseline is kept per-container.
    drake.on('drag', (dragged) => {
      scope.flipPositions = new Map(
        drake.containers.map((container) => [
          container,
          snapshotPositions(container, dragged),
        ]),
      );
    });
    drake.on('shadow', (dragged, container) => {
      const previous = scope.flipPositions.get(container) ?? new Map();
      scope.flipPositions.set(
        container,
        flipAnimate(container, previous, dragged),
      );
    });
    drake.on('dragend', () => {
      scope.flipPositions = new Map();
    });

    return drake;
  }

  @action
  registerContainer(name, element, list) {
    const scope = this.scopeFor(name);
    scope.containers.set(element, list);
    scope.drake.containers.push(element);
  }

  @action
  unregisterContainer(name, element) {
    const scope = this.scopes.get(name);
    if (!scope) return;

    scope.containers.delete(element);
    scope.flipPositions.delete(element);
    scope.drake.containers = scope.drake.containers.filter(
      (container) => container !== element,
    );

    if (scope.drake.containers.length === 0) {
      scope.drake.destroy();
      this.scopes.delete(name);
    }
  }

  accepts(scope, target, source) {
    if (target === source) return true;

    const sourceList = scope.containers.get(source);
    const targetList = scope.containers.get(target);
    if (!sourceList || !targetList) return false;

    // Two different lists can only share a scope when a page opted them in.
    if (sourceList !== targetList) return true;

    // Within one list, moving between groups means re-filing the item. That has
    // always been allowed for workstream groups; assignee groups only accept it
    // on a page that asked for cross-list dragging, so grouping the weekly-goals
    // list by assignee doesn't quietly turn a reorder into a re-assignment.
    return (
      targetList.groupBy === 'workstream' || Boolean(targetList.args.dragScope)
    );
  }

  // The person or workstream a drop named, or null when the target list is
  // ungrouped (or the item landed in the catch-all group).
  groupRecordFor(list, container) {
    const groupId = container.dataset.groupId;
    if (!groupId) return null;
    if (list.groupBy === 'assignee') {
      return this.store.peekRecord('person', groupId);
    }
    if (list.groupBy === 'workstream') {
      return this.store.peekRecord('workstream', groupId);
    }
    return null;
  }

  onDrop(scope, el, target, source) {
    const movedId = el.dataset.tfoId;
    const sourceList = scope.containers.get(source);
    const targetList = scope.containers.get(target);
    if (!movedId || !sourceList || !targetList) return;

    const sourceType = sourceList.args.type;
    const targetType = targetList.args.type;
    const moved = this.store.peekRecord(sourceType, movedId);
    if (!moved) return;

    // Neighbours are read from the target container, so they are records of the
    // target list's type even when the item came from another section.
    const { beforeId, afterId } = neighborsOf(el, target, 'tfoId');
    const before = beforeId
      ? this.store.peekRecord(targetType, beforeId)
      : null;
    const after = afterId ? this.store.peekRecord(targetType, afterId) : null;
    const position = positionBetween(before?.position, after?.position);

    if (target === source) {
      moved.position = position;
      return moved.save();
    }

    // dragula physically relocates `el` into `target` as part of the drag
    // gesture, entirely outside Ember's rendering. For a same-container reorder
    // that's harmless — Ember's own re-render reconciles the same {{#each}}
    // block and ends up agreeing with where dragula put it. But a cross-group
    // move relocates `el` out of one {{#each}} block's tracked DOM into a
    // different block's subtree; Glimmer's teardown of the old block can't
    // find/detach a node a third party already moved, leaving an orphaned stale
    // copy behind. Undo dragula's raw move here so Ember's own re-render is the
    // sole author of the new DOM structure.
    el.remove();

    const group = this.groupRecordFor(targetList, target);

    if (sourceType === targetType) {
      moved.position = position;
      if (targetList.groupBy === 'workstream') moved.workstream = group;
      if (targetList.groupBy === 'assignee' && group) moved.assignees = [group];
      return moved.save();
    }

    return this.moveToList(moved, targetList, targetType, position, group);
  }

  async moveToList(moved, targetList, targetType, position, group) {
    if (!isConvertibleType(targetType)) return;

    const overrides = { position };
    if (targetList.groupBy === 'assignee' && group) {
      overrides.assignees = [group];
    }
    // Only `tfo` has a workstream; every other list type is ungrouped or
    // grouped by assignee, so there is nothing to carry over.
    if (targetType === 'tfo' && targetList.groupBy === 'workstream') {
      overrides.workstream = group;
    }

    try {
      await convertListItem(this.store, moved, targetType, overrides);
    } catch (error) {
      Sentry.captureException(error);
    }
  }
}
